import React from "react";
import { motion } from "framer-motion";
import {
  Drop,
  ShieldCheck,
  Thermometer,
  Buildings,
  House,
  Factory,
  ArrowUpRight,
  Check,
} from "@phosphor-icons/react";
import { SERVICES } from "@/lib/siteData";

const ICONS = {
  Drop,
  ShieldCheck,
  Thermometer,
  Buildings,
  House,
  Factory,
};

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#121212] grain py-20 md:py-28 overflow-hidden"
    >
      {/* Decorative floating red accent */}
      <div
        aria-hidden="true"
        className="absolute top-0 right-0 w-[520px] h-[520px] opacity-[0.06] pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, #c41e3a 0%, transparent 60%)",
        }}
      />

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-16">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-[2px] bg-[#c41e3a]" />
              <span className="font-mono-upper text-[11px] text-[#c41e3a]">
                What We Do
              </span>
            </div>
            <h2 className="font-display font-black text-white text-4xl md:text-6xl leading-[1] tracking-[-0.03em]">
              Six core services.
              <span className="block text-[#c41e3a]">
                One uncompromising standard.
              </span>
            </h2>
          </div>
          <p className="font-body text-white/65 text-base md:text-lg max-w-md leading-relaxed">
            From a single-family home to a 200,000&nbsp;ft² warehouse, every
            project gets the same engineering-first approach.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {SERVICES.map((s, idx) => {
            const Icon = ICONS[s.icon];
            return (
              <motion.div
                key={s.number}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (idx % 3) * 0.08 }}
                data-testid={`service-card-${idx}`}
                className="group relative bg-gradient-to-b from-[#1c1c1c] to-[#161616] border border-white/10 hover:border-[#c41e3a]/60 transition-all duration-500 overflow-hidden flex flex-col"
              >
                {/* Corner tick — top-left */}
                <span className="absolute top-0 left-0 w-4 h-[2px] bg-[#c41e3a]" />
                <span className="absolute top-0 left-0 w-[2px] h-4 bg-[#c41e3a]" />
                {/* Corner tick — bottom-right */}
                <span className="absolute bottom-0 right-0 w-4 h-[2px] bg-[#c41e3a] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-0 right-0 w-[2px] h-4 bg-[#c41e3a] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Red top hairline that extends on hover */}
                <span className="absolute top-0 left-4 right-0 h-[1px] bg-gradient-to-r from-[#c41e3a]/60 to-transparent" />

                {/* Header row: number + arrow */}
                <div className="flex items-start justify-between px-7 md:px-9 pt-8 md:pt-10">
                  <div className="flex items-center gap-3">
                    <span className="font-display font-black text-[#c41e3a] text-3xl md:text-4xl leading-none tracking-tighter">
                      {s.number}
                    </span>
                    <span className="font-mono-upper text-[10px] text-white/40 mt-1">
                      / Service
                    </span>
                  </div>
                  <span className="w-10 h-10 inline-flex items-center justify-center border border-white/10 group-hover:bg-[#c41e3a] group-hover:border-[#c41e3a] transition-colors">
                    <ArrowUpRight
                      size={16}
                      weight="bold"
                      className="text-white/60 group-hover:text-white transition-all duration-300 group-hover:-translate-y-[2px] group-hover:translate-x-[2px]"
                    />
                  </span>
                </div>

                {/* Divider */}
                <div className="mx-7 md:mx-9 mt-6 h-[1px] bg-white/10 group-hover:bg-[#c41e3a]/40 transition-colors duration-500" />

                {/* Icon + title + copy */}
                <div className="px-7 md:px-9 pt-7 pb-6 flex-1 flex flex-col">
                  <div className="flex items-start gap-4 mb-5">
                    <div className="relative shrink-0 w-14 h-14 flex items-center justify-center bg-[#c41e3a]/10 border border-[#c41e3a]/30 group-hover:bg-[#c41e3a] group-hover:border-[#c41e3a] transition-colors">
                      <Icon
                        size={26}
                        weight="duotone"
                        className="text-[#c41e3a] group-hover:text-white transition-colors"
                      />
                    </div>
                    <h3 className="font-display font-bold text-white text-xl md:text-2xl tracking-tight leading-[1.15] pt-1">
                      {s.title}
                    </h3>
                  </div>

                  <p className="font-body text-white/65 text-[15px] leading-relaxed">
                    {s.copy}
                  </p>

                  {/* Feature bullets */}
                  <ul className="mt-6 space-y-2.5">
                    {s.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2.5 font-body text-white/80 text-[13px] leading-snug"
                      >
                        <Check
                          size={14}
                          weight="bold"
                          className="text-[#c41e3a] shrink-0 mt-[3px]"
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Footer strip */}
                <div className="mt-auto px-7 md:px-9 py-4 border-t border-white/10 flex items-center justify-between">
                  <span className="font-mono-upper text-[10px] text-white/50 group-hover:text-[#c41e3a] transition-colors">
                    Learn more
                  </span>
                  <span className="font-mono-upper text-[10px] text-white/35">
                    {String(idx + 1).padStart(2, "0")} / 06
                  </span>
                </div>

                {/* Hover red left accent */}
                <span className="absolute left-0 top-0 bottom-0 w-0 bg-[#c41e3a] group-hover:w-[3px] transition-all duration-500" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
